import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { loadStripe } from '@stripe/stripe-js';

const API_URL = API_BASE_URL;

// Initialize Stripe - publishable key will be fetched from backend
let stripePromise = null;

/**
 * Billing Service
 *
 * Handles all billing-related API calls including:
 * - Payment methods management
 * - Subscription operations
 * - Invoice retrieval
 * - Stripe integration
 */
class BillingService {
  /**
   * Get or initialize Stripe instance
   */
  async getStripe() {
    if (!stripePromise) {
      // Fetch publishable key from backend
      try {
        const response = await axios.get(`${API_URL}/billing/stripe-config`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        });
        stripePromise = loadStripe(response.data.publishableKey);
      } catch (error) {
        console.error('Failed to load Stripe configuration:', error);
        throw new Error('Unable to initialize payment system');
      }
    }
    return stripePromise;
  }

  // ==================== Payment Methods ====================

  /**
   * Get all payment methods for an organization
   */
  async getPaymentMethods(organizationId) {
    if (!organizationId || organizationId === '') {
      console.warn('getPaymentMethods called without organizationId:', organizationId);
      return { paymentMethods: [] };
    }

    const url = `${API_URL}/billing/organizations/${organizationId}/payment-methods`;
    console.log('Fetching payment methods from:', url);

    try {
      const response = await axios.get(
        url,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Create a setup intent for adding a new payment method
   */
  async createSetupIntent(organizationId) {
    try {
      const response = await axios.post(
        `${API_URL}/billing/setup-intent`,
        { organizationId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating setup intent:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Save a payment method after successful setup
   */
  async savePaymentMethod(organizationId, customerId, paymentMethodId, setAsDefault = false) {
    try {
      const response = await axios.post(
        `${API_URL}/billing/payment-methods`,
        {
          organizationId,
          customerId,
          paymentMethodId,
          setAsDefault
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving payment method:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Remove a payment method
   */
  async removePaymentMethod(organizationId, paymentMethodId) {
    try {
      const response = await axios.delete(
        `${API_URL}/billing/payment-methods/${paymentMethodId}`,
        {
          data: { organizationId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error removing payment method:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Set a payment method as default
   */
  async setDefaultPaymentMethod(organizationId, paymentMethodId) {
    try {
      const response = await axios.put(
        `${API_URL}/billing/payment-methods/${paymentMethodId}/default`,
        { organizationId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error setting default payment method:', error);
      throw error.response?.data || error;
    }
  }

  // ==================== Subscriptions ====================

  /**
   * Get available subscriptions (assigned by admin)
   */
  async getAvailableSubscriptions(organizationId) {
    if (!organizationId) {
      console.warn('getAvailableSubscriptions called without organizationId');
      return { availableSubscriptions: [] };
    }

    try {
      const response = await axios.get(
        `${API_URL}/user/subscriptions/${organizationId}/available`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching available subscriptions:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Get active subscriptions
   */
  async getActiveSubscriptions(organizationId) {
    if (!organizationId) {
      console.warn('getActiveSubscriptions called without organizationId');
      return { activeSubscriptions: [] };
    }

    try {
      const response = await axios.get(
        `${API_URL}/user/subscriptions/${organizationId}/active`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching active subscriptions:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Activate a subscription with payment
   */
  async activateSubscription(organizationId, subscriptionTypeId, sensorQuantity, paymentMethodId) {
    try {
      const response = await axios.post(
        `${API_URL}/user/subscriptions/${organizationId}/activate/${subscriptionTypeId}`,
        {
          sensorQuantity,
          paymentMethodId
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error activating subscription:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Increase subscription quantity (immediate charge)
   */
  async increaseSubscriptionQuantity(organizationId, subscriptionId, newQuantity) {
    try {
      const response = await axios.put(
        `${API_URL}/user/subscriptions/${organizationId}/${subscriptionId}/increase-quantity`,
        { newQuantity },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error increasing quantity:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Decrease subscription quantity (scheduled for next period)
   */
  async decreaseSubscriptionQuantity(organizationId, subscriptionId, newQuantity) {
    try {
      const response = await axios.put(
        `${API_URL}/user/subscriptions/${organizationId}/${subscriptionId}/decrease-quantity`,
        { newQuantity },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error decreasing quantity:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Cancel scheduled quantity change
   */
  async cancelScheduledQuantityChange(organizationId, subscriptionId) {
    try {
      const response = await axios.delete(
        `${API_URL}/user/subscriptions/${organizationId}/${subscriptionId}/scheduled-change`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error cancelling scheduled change:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Cancel subscription at period end
   */
  async cancelSubscription(organizationId, subscriptionId, reason) {
    try {
      const response = await axios.post(
        `${API_URL}/user/subscriptions/${organizationId}/${subscriptionId}/cancel`,
        { reason },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Reactivate a cancelled subscription
   */
  async reactivateSubscription(organizationId, subscriptionId) {
    try {
      const response = await axios.post(
        `${API_URL}/user/subscriptions/${organizationId}/${subscriptionId}/reactivate`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Preview quantity change proration
   */
  async previewQuantityChange(organizationId, subscriptionId, newQuantity) {
    try {
      const response = await axios.post(
        `${API_URL}/user/subscriptions/${organizationId}/${subscriptionId}/preview-quantity-change`,
        { newQuantity },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error previewing quantity change:', error);
      throw error.response?.data || error;
    }
  }

  // ==================== Invoices ====================

  /**
   * Get invoice history
   */
  async getInvoices(organizationId, limit = 50) {
    if (!organizationId) {
      console.warn('getInvoices called without organizationId');
      return { invoices: [] };
    }

    try {
      const response = await axios.get(
        `${API_URL}/billing/${organizationId}/invoices`,
        {
          params: { limit },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Download invoice PDF
   */
  async downloadInvoice(organizationId, invoiceId) {
    try {
      const response = await axios.get(
        `${API_URL}/billing/${organizationId}/invoices/${invoiceId}/download`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Error downloading invoice:', error);
      throw error.response?.data || error;
    }
  }

  // ==================== Usage & Analytics ====================

  /**
   * Get subscription usage data
   */
  async getSubscriptionUsage(organizationId, subscriptionId, startDate, endDate) {
    try {
      const response = await axios.get(
        `${API_URL}/billing/${organizationId}/subscriptions/${subscriptionId}/usage`,
        {
          params: { startDate, endDate },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching usage data:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Get sensor assignment breakdown
   */
  async getSensorAssignments(organizationId, subscriptionId) {
    try {
      const response = await axios.get(
        `${API_URL}/billing/${organizationId}/subscriptions/${subscriptionId}/sensor-assignments`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching sensor assignments:', error);
      throw error.response?.data || error;
    }
  }
}

// Export singleton instance
const billingService = new BillingService();
export default billingService;